import { useEffect, useRef, useState } from 'react';
import { createAuth0Client, type Auth0Client } from '@auth0/auth0-spa-js';
import { CN_AUTH0_CONFIG } from 'shared/utils/constants';
import { isGhostingAsCn } from 'shared/utils/partnerUser';
import { fetchPartnerGhostLogin } from 'shared/utils/partnerGhostLogin';
import { logError } from 'shared/utils/logError';
import { type FeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import {
  clearPartnerSession,
  fetchAccountFeatures,
  fetchPartnerLogin,
  persistPartnerSession,
  type PartnerLoginData,
} from './utils';

declare global {
  interface Window {
    resetCNWidget?: () => void;
  }
}

const SESSION_EXPIRED = 'Session expired';
const redirectUri = () => `${window.location.origin}/cn-widget`;

const cnLoginError = (status: number) =>
  status === 401 ? SESSION_EXPIRED : `Error ${status}`;

const toErrorMessage = (err: unknown) =>
  err instanceof Error ? err.message : 'Unknown error';

/**
 * Get an Auth0 access token without user interaction.
 * Returns null when the user must log in manually (incognito / blocked cookies).
 */
const getSilentAccessToken = async (client: Auth0Client) => {
  const audience = CN_AUTH0_CONFIG.audience;
  if (!(await client.isAuthenticated())) {
    try {
      await client.getTokenSilently({ authorizationParams: { audience } });
    } catch (err) {
      const code = (err as { error?: string })?.error;
      console.log('Silent login failed:', code, err);
      if (code === 'login_required' || code === 'consent_required') return null;
      throw err;
    }
  }
  return client.getTokenSilently({ authorizationParams: { audience } });
};

export interface CnWidgetAuth {
  isLoading: boolean;
  error: string;
  /** Silent auth failed; the user must click "Log in". */
  showLoginButton: boolean;
  loginData: PartnerLoginData | null;
  features: FeatureFlags;
  login: () => Promise<void>;
}

/**
 * Charity Navigator widget authentication: Auth0 (or super-admin ghosting)
 * -> TrueImpact session -> account feature flags.
 * Persists the session to localStorage unless ghosting.
 */
export const useCnWidgetAuth = (): CnWidgetAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLoginButton, setShowLoginButton] = useState(false);
  const [loginData, setLoginData] = useState<PartnerLoginData | null>(null);
  const [features, setFeatures] = useState<FeatureFlags>({});
  const auth0Ref = useRef<Auth0Client | null>(null);

  // Expose global function for debugging/testing from console
  useEffect(() => {
    window.resetCNWidget = () => {
      clearPartnerSession();
      window.location.href = '/cn-widget?reset=true';
    };
    return () => {
      delete window.resetCNWidget;
    };
  }, []);

  useEffect(() => {
    const ghosting = isGhostingAsCn();

    const loginWithAuth0 = async () => {
      const client = await createAuth0Client({
        domain: CN_AUTH0_CONFIG.domain,
        clientId: CN_AUTH0_CONFIG.clientId,
        authorizationParams: {
          audience: CN_AUTH0_CONFIG.audience,
          redirect_uri: redirectUri(),
        },
      });
      auth0Ref.current = client;
      const accessToken = await getSilentAccessToken(client);
      if (!accessToken) return null;
      return fetchPartnerLogin('cn', accessToken, cnLoginError);
    };

    const run = async () => {
      try {
        const data = ghosting
          ? await fetchPartnerGhostLogin('cn')
          : await loginWithAuth0();
        if (!data) {
          setShowLoginButton(true);
          return;
        }
        const accountFeatures = await fetchAccountFeatures(data.ti_token);
        if (!ghosting) persistPartnerSession(data, 'cn', accountFeatures);
        setFeatures(accountFeatures);
        setLoginData(data);
      } catch (err) {
        const message = toErrorMessage(err);
        if (ghosting) {
          console.error('CN ghost login failed:', err);
          setError(`Ghost login failed: ${message}`);
        } else if (message === SESSION_EXPIRED) {
          logError('CN Auth failed:', err);
          clearPartnerSession();
          setError('Your session has expired. Please log in again.');
        } else {
          logError('CN Auth failed:', err);
          setError(`Authentication failed: ${message}`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    run();
  }, []);

  const login = async () => {
    if (!auth0Ref.current) return;
    try {
      await auth0Ref.current.loginWithRedirect({
        authorizationParams: {
          audience: CN_AUTH0_CONFIG.audience,
          redirect_uri: redirectUri(),
        },
      });
    } catch (err) {
      setError(`Login failed: ${toErrorMessage(err)}`);
    }
  };

  return { isLoading, error, showLoginButton, loginData, features, login };
};
